import { useState, useEffect, useCallback } from 'react'
import {
  Loader2, Users, Truck, Star, Wallet, Clock, Power, Building2, AlertTriangle,
} from 'lucide-react'
import toast from 'react-hot-toast'
import {
  fetchCompanyProfile, fetchCompanyCollectors, updateCompanyCollector,
  fetchCompanyPickups, fetchCompanyAssignments, fetchCompanyEarnings,
  type CompanyProfileResponse, type CompanyCollectorRow, type CompanyEarningsResponse,
} from '../services/companyService'
import type { PickupAssignment, Pickup } from '../types'
import CompanyPayouts from './CompanyPayouts'
import { money } from '../utils/money'

type Tab = 'overview' | 'collectors' | 'pickups' | 'offers' | 'earnings' | 'payouts'

const TABS: { value: Tab; label: string }[] = [
  { value: 'overview', label: 'Overview' },
  { value: 'collectors', label: 'Collectors' },
  { value: 'pickups', label: 'Pickups' },
  { value: 'offers', label: 'Offers' },
  { value: 'earnings', label: 'Earnings' },
  { value: 'payouts', label: 'Payouts' },
]

function StatCard({
  label, value, icon: Icon, color,
}: {
  label: string
  value: string | number
  icon: typeof Users
  color: string
}) {
  return (
    <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 flex items-center gap-3">
      <div className={`w-10 h-10 rounded-xl ${color.split(' ')[1]} flex items-center justify-center`}>
        <Icon className={`w-5 h-5 ${color.split(' ')[0]}`} />
      </div>
      <div>
        <p className="text-xs text-[#6B7280] font-medium">{label}</p>
        <p className="text-xl font-bold text-[#1A1A2E]">{value}</p>
      </div>
    </div>
  )
}


export default function CompanyPortal() {
  const [tab, setTab] = useState<Tab>('overview')
  const [profile, setProfile] = useState<CompanyProfileResponse | null>(null)
  const [collectors, setCollectors] = useState<CompanyCollectorRow[]>([])
  const [pickups, setPickups] = useState<Pickup[]>([])
  const [assignments, setAssignments] = useState<PickupAssignment[]>([])
  const [earnings, setEarnings] = useState<CompanyEarningsResponse | null>(null)
  const [earningsState, setEarningsState] = useState<'idle' | 'loading' | 'error'>('idle')
  const [pickupPage, setPickupPage] = useState(1)
  const [pickupPages, setPickupPages] = useState(1)
  const [pickupTotal, setPickupTotal] = useState(0)
  const [pickupStatus, setPickupStatus] = useState<string>('ALL')
  const [loading, setLoading] = useState(true)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [notLinked, setNotLinked] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const data = await fetchCompanyProfile()
      setProfile(data)
      setNotLinked(false)
    } catch (err) {
      // A company admin whose company was deleted has no scope at all; say so
      // plainly instead of showing an empty dashboard.
      if ((err as { response?: { status?: number } })?.response?.status === 403) {
        setNotLinked(true)
      } else {
        toast.error('Failed to load your company')
      }
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  useEffect(() => {
    if (notLinked) return
    if (tab === 'collectors') {
      fetchCompanyCollectors().then((d) => setCollectors(d.collectors ?? [])).catch(() => toast.error('Failed to load collectors'))
    } else if (tab === 'pickups') {
      fetchCompanyPickups({ limit: 25, page: pickupPage, status: pickupStatus === 'ALL' ? undefined : pickupStatus })
        .then((d) => {
          setPickups(d?.pickups ?? [])
          setPickupPages(d?.pagination?.totalPages ?? 1)
          setPickupTotal(d?.pagination?.total ?? (d?.pickups ?? []).length)
        })
        .catch(() => toast.error('Failed to load pickups'))
    } else if (tab === 'offers') {
      fetchCompanyAssignments({ status: 'ALL', limit: 50 }).then((d) => setAssignments(d?.assignments ?? [])).catch(() => toast.error('Failed to load offers'))
    } else if (tab === 'earnings') {
      setEarningsState('loading')
      fetchCompanyEarnings()
        .then((d) => { setEarnings(d); setEarningsState('idle') })
        .catch(() => { setEarningsState('error'); toast.error('Failed to load earnings') })
    }
  }, [tab, notLinked, pickupPage, pickupStatus])

  const handleToggleDuty = async (collector: CompanyCollectorRow) => {
    setBusyId(collector.id)
    try {
      await updateCompanyCollector(collector.id, { isAvailable: !collector.isAvailable })
      setCollectors((prev) =>
        prev.map((c) => (c.id === collector.id ? { ...c, isAvailable: !c.isAvailable } : c)),
      )
      toast.success(`${collector.name} is now ${collector.isAvailable ? 'off' : 'on'} duty`)
      // The overview's on-duty and pending-offer counters come from the profile.
      load()
    } catch (err) {
      // The API says why — a suspended company, a collector who is no longer
      // yours — and that is more useful than a generic failure.
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
      toast.error(message || 'Failed to update duty status')
    } finally {
      setBusyId(null)
    }
  }

  const handleCapacity = async (collector: CompanyCollectorRow, value: number, input?: HTMLInputElement) => {
    if (!Number.isFinite(value) || value < 1 || value > 100) {
      if (input) input.value = String(collector.maxDailyPickups)
      return
    }
    setBusyId(collector.id)
    try {
      await updateCompanyCollector(collector.id, { maxDailyPickups: value })
      setCollectors((prev) =>
        prev.map((c) => (c.id === collector.id ? { ...c, maxDailyPickups: value } : c)),
      )
      toast.success(`${collector.name}'s daily capacity set to ${value}`)
    } catch (err) {
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
      toast.error(message || 'Failed to update capacity')
      // Put the number the server still holds back in the box.
      if (input) input.value = String(collector.maxDailyPickups)
    } finally {
      setBusyId(null)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    )
  }

  if (notLinked || !profile) {
    return (
      <div className="bg-white rounded-2xl p-12 shadow-sm border border-gray-100 text-center">
        <AlertTriangle className="w-10 h-10 text-orange-400 mx-auto mb-3" />
        <p className="text-sm font-semibold text-[#1A1A2E]">Your account is not linked to a company</p>
        <p className="text-xs text-[#6B7280] mt-1">
          Ask a SmartWasteCycle administrator to attach your account to your company.
        </p>
      </div>
    )
  }

  const suspended = profile.company.status === 'SUSPENDED'

  return (
    <div className="space-y-5">
      {suspended && (
        <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-2xl p-4">
          <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-bold text-red-700">{profile.company.name} is suspended</p>
            <p className="text-xs text-red-600 mt-0.5">
              Your collectors are not receiving new pickup offers, and nothing here can be changed
              while the suspension stands. You can still review your collectors and your work.
              Contact SmartWasteCycle to resolve this.
            </p>
          </div>
        </div>
      )}

      {/* Company header */}
      <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 flex items-start gap-4">
        <div className="w-12 h-12 rounded-xl bg-primary-50 flex items-center justify-center flex-shrink-0">
          <Building2 className="w-6 h-6 text-primary-600" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-lg font-bold text-[#1A1A2E]">{profile.company.name}</p>
          <p className="text-xs text-[#6B7280]">
            {profile.company.contactName} · {profile.company.contactEmail}
            {profile.company.contactPhone ? ` · ${profile.company.contactPhone}` : ''}
          </p>
          <div className="flex flex-wrap gap-1 mt-2">
            {profile.company.serviceZones.length === 0 ? (
              <span className="text-[10px] text-orange-500 italic">No service zones set</span>
            ) : (
              profile.company.serviceZones.map((zone) => (
                <span key={zone} className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">
                  {zone}
                </span>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm w-fit">
        {TABS.map(({ value, label }) => (
          <button
            key={value}
            onClick={() => setTab(value)}
            className={`px-4 py-2 text-xs font-semibold transition-colors ${
              tab === value ? 'bg-primary-600 text-white' : 'text-[#6B7280] hover:bg-gray-50'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'overview' && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
            <StatCard label="Collectors" value={profile.collectors.total} icon={Users} color="text-blue-600 bg-blue-50" />
            <StatCard label="On duty" value={profile.collectors.onDuty} icon={Power} color="text-green-700 bg-green-50" />
            <StatCard label="Offers waiting" value={profile.metrics.pendingOffers} icon={Clock} color="text-orange-600 bg-orange-50" />
            <StatCard
              label={`Rating (${profile.metrics.totalRatings})`}
              value={profile.metrics.totalRatings > 0 ? `${profile.metrics.averageRating} / 5` : '—'}
              icon={Star}
              color="text-yellow-600 bg-yellow-50"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
            <StatCard label="Pickups all time" value={profile.metrics.totalPickups} icon={Truck} color="text-purple-600 bg-purple-50" />
            <StatCard label="Completed" value={profile.metrics.completedPickups} icon={Truck} color="text-green-700 bg-green-50" />
            <StatCard label="In flight" value={profile.metrics.activePickups} icon={Truck} color="text-blue-600 bg-blue-50" />
            <StatCard label="Completion rate" value={`${profile.metrics.completionRate}%`} icon={Truck} color="text-primary-600 bg-primary-50" />
          </div>
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
            <p className="text-xs font-bold text-[#6B7280] uppercase tracking-wide mb-3">Last 30 days</p>
            <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
              {[
                { label: 'Pickups', value: profile.last30Days.total, color: 'text-blue-600 bg-blue-50' },
                { label: 'Completed', value: profile.last30Days.completed, color: 'text-green-700 bg-green-50' },
                { label: 'In flight', value: profile.last30Days.active, color: 'text-purple-600 bg-purple-50' },
                { label: 'Missed', value: profile.last30Days.missed, color: 'text-red-600 bg-red-50' },
              ].map(({ label, value, color }) => (
                <div key={label} className={`rounded-xl p-3 text-center ${color.split(' ')[1]}`}>
                  <p className={`text-2xl font-bold ${color.split(' ')[0]}`}>{value}</p>
                  <p className={`text-[10px] font-medium ${color.split(' ')[0]} opacity-80`}>{label}</p>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {tab === 'collectors' && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          {collectors.length === 0 ? (
            <p className="py-12 text-center text-sm text-[#6B7280]">
              No collectors are attached to your company yet.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-[#F8FAF9]">
                    {['Collector', 'Zone', 'Duty', 'Capacity', 'Active', 'Completed', 'Rating', ''].map((h) => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-[#6B7280] whitespace-nowrap">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {collectors.map((c) => (
                    <tr key={c.id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-4 py-3">
                        <p className="text-xs font-semibold text-[#1A1A2E]">{c.name}</p>
                        <p className="text-[11px] text-[#6B7280]">{c.phone || c.email}</p>
                      </td>
                      <td className="px-4 py-3 text-xs text-[#6B7280]">{c.zone ?? '—'}</td>
                      <td className="px-4 py-3">
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${c.isAvailable ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                          {c.isAvailable ? 'On duty' : 'Off duty'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <input
                          type="number"
                          min={1}
                          max={100}
                          defaultValue={c.maxDailyPickups}
                          disabled={busyId === c.id || suspended}
                          aria-label={`Daily capacity for ${c.name}`}
                          onBlur={(e) => {
                            const value = Number(e.target.value)
                            if (value !== c.maxDailyPickups) handleCapacity(c, value, e.currentTarget)
                          }}
                          className="w-16 border border-gray-200 rounded-lg px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                      </td>
                      <td className="px-4 py-3 text-xs text-[#6B7280]">
                        {c.metrics.activePickups}
                        {c.metrics.pendingOffers > 0 && (
                          <span className="ml-1 text-orange-600">(+{c.metrics.pendingOffers} offered)</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-[#6B7280]">
                        {c.metrics.completedPickups} ({c.metrics.completionRate}%)
                      </td>
                      <td className="px-4 py-3 text-xs text-[#6B7280]">
                        {c.metrics.totalRatings > 0 ? `${c.metrics.averageRating}★` : '—'}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          disabled={busyId === c.id || suspended}
                          onClick={() => handleToggleDuty(c)}
                          className="flex items-center gap-1 text-xs font-semibold text-primary-700 hover:bg-primary-50 px-2 py-1 rounded-lg transition disabled:opacity-50"
                        >
                          <Power size={12} />
                          {c.isAvailable ? 'Stand down' : 'Put on duty'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === 'pickups' && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-gray-100">
            <p className="text-xs text-[#6B7280]">{pickupTotal} pickup{pickupTotal === 1 ? '' : 's'}</p>
            <select
              value={pickupStatus}
              onChange={(e) => { setPickupStatus(e.target.value); setPickupPage(1) }}
              aria-label="Filter pickups by status"
              className="text-xs border border-gray-200 rounded-lg px-2 py-1.5"
            >
              {['ALL', 'PENDING', 'SCHEDULED', 'IN_PROGRESS', 'COMPLETED', 'MISSED', 'CANCELLED'].map((st) => (
                <option key={st} value={st}>{st === 'ALL' ? 'All statuses' : st.replace('_', ' ')}</option>
              ))}
            </select>
          </div>
          {pickups.length === 0 ? (
            <p className="py-12 text-center text-sm text-[#6B7280]">No pickups yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-[#F8FAF9]">
                    {['Address', 'Waste', 'Collector', 'Scheduled', 'Status'].map((h) => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-[#6B7280]">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {pickups.map((p) => (
                    <tr key={p.id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-4 py-3 text-xs text-[#1A1A2E] max-w-[260px] truncate">{p.address}</td>
                      <td className="px-4 py-3 text-xs text-[#6B7280]">{p.wasteType}</td>
                      <td className="px-4 py-3 text-xs text-[#6B7280]">{p.collector?.name ?? '—'}</td>
                      <td className="px-4 py-3 text-xs text-[#6B7280] whitespace-nowrap">
                        {new Date(p.scheduledDate).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3 text-xs font-semibold text-[#1A1A2E]">
                        {p.status.replace('_', ' ')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {pickupPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
              <p className="text-xs text-[#6B7280]">Page {pickupPage} of {pickupPages}</p>
              <div className="flex gap-2">
                <button type="button" disabled={pickupPage <= 1} onClick={() => setPickupPage((p) => p - 1)} className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-gray-200 disabled:opacity-40">Previous</button>
                <button type="button" disabled={pickupPage >= pickupPages} onClick={() => setPickupPage((p) => p + 1)} className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-gray-200 disabled:opacity-40">Next</button>
              </div>
            </div>
          )}
        </div>
      )}

      {tab === 'offers' && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          {assignments.length === 0 ? (
            <p className="py-12 text-center text-sm text-[#6B7280]">
              No pickup offers have reached your collectors yet.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-[#F8FAF9]">
                    {['Pickup', 'Collector', 'Offered', 'Status', 'Reason'].map((h) => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-[#6B7280]">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {assignments.map((a) => (
                    <tr key={a.id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-4 py-3 text-xs text-[#1A1A2E] max-w-[240px] truncate">
                        {a.pickup?.address ?? a.pickupId.slice(0, 8)}
                      </td>
                      <td className="px-4 py-3 text-xs text-[#6B7280]">{a.collector?.name ?? '—'}</td>
                      <td className="px-4 py-3 text-xs text-[#6B7280] whitespace-nowrap">
                        {new Date(a.offeredAt).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-xs font-semibold text-[#1A1A2E]">
                        {a.status.charAt(0) + a.status.slice(1).toLowerCase()}
                      </td>
                      <td className="px-4 py-3 text-xs text-[#6B7280] max-w-[180px] truncate">
                        {a.declineReason ?? '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === 'payouts' && <CompanyPayouts suspended={suspended} />}

      {tab === 'earnings' && earningsState === 'loading' && !earnings && (
        <div className="flex items-center justify-center h-40"><Loader2 className="w-6 h-6 animate-spin text-primary-600" /></div>
      )}
      {tab === 'earnings' && earningsState === 'error' && !earnings && (
        <div className="bg-white rounded-2xl p-8 text-center shadow-sm border border-gray-100">
          <p className="text-sm text-[#6B7280]">Earnings could not be loaded.</p>
          <button type="button" onClick={() => setTab('overview')} className="mt-3 text-xs font-semibold text-primary-700">Back to overview</button>
        </div>
      )}
      {tab === 'earnings' && earnings && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
            <StatCard label="Completed pickups" value={earnings.completedCount} icon={Truck} color="text-blue-600 bg-blue-50" />
            <StatCard label="Gross earned" value={money(earnings.totalEarned, earnings.currency)} icon={Wallet} color="text-green-700 bg-green-50" />
            <StatCard
              label={`Your share (${Math.round(earnings.commissionRate * 100)}%)`}
              value={money(earnings.companyShare, earnings.currency)}
              icon={Building2}
              color="text-purple-600 bg-purple-50"
            />
            <StatCard label="Collectors' share" value={money(earnings.collectorShare, earnings.currency)} icon={Users} color="text-orange-600 bg-orange-50" />
          </div>
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
              <div>
                <p className="text-xs text-[#6B7280]">Rate per completed pickup</p>
                <p className="font-bold text-[#1A1A2E]">{money(earnings.payRate, earnings.currency)}</p>
              </div>
              <div>
                <p className="text-xs text-[#6B7280]">Paid out to collectors</p>
                <p className="font-bold text-[#1A1A2E]">{money(earnings.totalPaidOut, earnings.currency)}</p>
              </div>
              <div>
                <p className="text-xs text-[#6B7280]">Payouts in progress</p>
                <p className="font-bold text-[#1A1A2E]">{money(earnings.pendingPayouts, earnings.currency)}</p>
              </div>
            </div>
            {earnings.settlement && (
              <p className="text-xs text-[#6B7280] border-t border-gray-100 pt-3">
                {earnings.settlement}
              </p>
            )}
          </div>
        </>
      )}
    </div>
  )
}
